// src/components/Settings/Layout/SelfDetail.js

import React, { useState, useRef, useEffect } from "react";
import "../Styles/SelfDetail.css";
import ImageSettingsPopup from "../Utils/ImageSettingsPopup";
import userPic from "../../../../../assets/images/santosh-kumar.jpeg"

// ==================== CONSTANTS ====================

const DEFAULT_PUBLIC_VISIBILITY = {
  name:     true,
  location: true,
  image:    true,
  friends:  true,
  groups:   true,
  posts:    true,
};

const DEFAULT_PRIVATE_EXPOSURE = {
  profileList:    false,
  accountDetails: false,
  emailId:        false,
  mobileNumber:   false,
};

const PRIVATE_RISK_MESSAGES = {
  profileList:    "⚠️ Exposing your Profile List allows others to see all profiles linked to your account. Proceed with caution.",
  accountDetails: "⚠️ Exposing Account Details may reveal sensitive subscription and billing information to other users.",
  emailId:        "⚠️ Your Email ID is sensitive. Making it public may lead to spam or phishing attempts. Are you sure?",
  mobileNumber:   "⚠️ Sharing your Mobile Number publicly is a significant privacy risk. This cannot be undone automatically.",
};

// ── Label map for image visibility display ────────────────────────────────
const IMAGE_VISIBILITY_LABELS = {
  public:  { text: "Public",             color: "#81c784", bg: "rgba(76,175,80,0.15)",    border: "rgba(76,175,80,0.3)"    },
  friends: { text: "Friends & Family",   color: "#64b5f6", bg: "rgba(33,150,243,0.15)",   border: "rgba(33,150,243,0.3)"   },
  none:    { text: "Hidden",             color: "rgba(255,255,255,0.38)", bg: "rgba(120,120,120,0.15)", border: "rgba(255,255,255,0.10)" },
};

// ==================== SUB-COMPONENTS ====================

// ── Visibility toggle row ─────────────────────────────────────────────────
const VisibilityRow = ({ label, fieldKey, isVisible, onToggle, icon, sublabel }) => (
  <div className="sd-field-row">
    <div className="sd-field-left">
      <span className="sd-field-icon">{icon}</span>
      <div className="sd-field-info">
        <span className="sd-field-label">{label}</span>
        {sublabel && <span className="sd-field-sublabel">{sublabel}</span>}
      </div>
    </div>
    <div className="sd-field-right">
      <span className={`sd-visibility-tag ${isVisible ? "sd-vis--public" : "sd-vis--hidden"}`} style={{background: "none", border: "1px solid gray", color: "white"}}>
        {isVisible ? "Visible" : "Hidden"}
      </span>
      <label className="st-switch">
        <input type="checkbox" checked={isVisible} onChange={() => onToggle(fieldKey)} />
        <span className="st-slider" />
      </label>
    </div>
  </div>
);

// ── Private exposure row ─────────────────────────────────────────────────
const PrivateRow = ({ label, fieldKey, isExposed, onToggle, icon, sublabel }) => (
  <div className={`sd-field-row sd-field-row--private ${isExposed ? "sd-field-row--exposed" : ""}`}>
    <div className="sd-field-left">
      <span className="sd-field-icon">{icon}</span>
      <div className="sd-field-info">
        <span className="sd-field-label">{label}</span>
        {sublabel && <span className="sd-field-sublabel">{sublabel}</span>}
        <span className="sd-private-badge">🔒 Private</span>
      </div>
    </div>
    <div className="sd-field-right">
      <span className={`sd-visibility-tag ${isExposed ? "sd-vis--exposed" : "sd-vis--private"}`}>
        {isExposed ? "⚠️ Exposed" : "Protected"}
      </span>
      <label className="st-switch">
        <input type="checkbox" checked={isExposed} onChange={() => onToggle(fieldKey)} />
        <span className={`st-slider ${isExposed ? "st-slider--danger" : ""}`} />
      </label>
    </div>
  </div>
);

// ── Image uploader ────────────────────────────────────────────────────────
const ImageUploader = ({ currentImage, onImageChange }) => {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(currentImage || null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreview(ev.target.result);
      onImageChange(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="sd-image-uploader">
      <div className="sd-image-preview-wrap">
        {preview ? (
          <img src={preview} alt="Profile" className="sd-image-preview" />
        ) : (
          <div className="sd-image-placeholder">
            <span style={{ fontSize: 36 }}>👤</span>
          </div>
        )}
        <button
          type="button"
          className="sd-image-change-btn"
          onClick={() => fileRef.current?.click()}
          title="Change profile image"
        >
          📷 Change
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <p className="sd-image-hint">JPG, PNG or GIF · Max 5 MB</p>
    </div>
  );
};

// ── Section header ────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title, subtitle, badge }) => (
  <div className="sd-section-header">
    <div className="sd-section-header-left">
      <span className="sd-section-icon">{icon}</span>
      <div>
        <h3 className="sd-section-title">{title}</h3>
        <p  className="sd-section-subtitle">{subtitle}</p>
      </div>
    </div>
    {badge && <span className="sd-section-badge" style={{background: "none", color: "white", border: "1px solid gray"}}>{badge}</span>}
  </div>
);

// ── Clickable image visibility trigger ───────────────────────────────────
const ImageVisibilityTrigger = ({ imageVisibility, allowMaximize, onOpen }) => {
  const meta = IMAGE_VISIBILITY_LABELS[imageVisibility] || IMAGE_VISIBILITY_LABELS.public;

  return (
    <button
      type="button"
      className="sd-img-vis-trigger"
      onClick={onOpen}
      title="Configure image visibility settings"
    >
      <div className="sd-img-vis-trigger-top">
        <div className="sd-img-vis-trigger-info">
          <span className="sd-img-vis-trigger-label">Profile Image Settings</span>
          <span className="sd-img-vis-trigger-sublabel">
            Who can see it · Virtual image · Click to maximise
          </span>
        </div>
        <span className="sd-img-vis-trigger-arrow">›</span>
      </div>

      {/* Live preview of current settings */}
      <div className="sd-img-vis-trigger-status">
        <span
          className="sd-visibility-tag"
          style={{
            color:      "white",
            background: "rgba(36,36,45,0.2)",
            border:     `1px solid white`,
          }}
        >
          👁 {meta.text}
        </span>
        <span className="sd-img-vis-chip">
          {allowMaximize ? "🔍 Click to zoom ON" : "🔍 Click to zoom OFF"}
        </span>
      </div>
    </button>
  );
};

// ==================== MAIN COMPONENT ====================

const SelfDetail = ({ userid }) => {

  // ── Form state ────────────────────────────────────────────────────────
  const [publicForm, setPublicForm] = useState({
    name:     "",
    location: "",
    image:    null,
    friends:  "0",
    groups:   "0",
    posts:    "0",
  });

  const [publicVisibility, setPublicVisibility] = useState(DEFAULT_PUBLIC_VISIBILITY);
  const [privateData]                           = useState({
    profileList:    "3 profiles linked",
    accountDetails: "Free Tier · Joined Jan 2024",
    emailId:        "u••••••@example.com",
    mobileNumber:   "+•• •••• ••••••",
  });
  const [privateExposure, setPrivateExposure]   = useState(DEFAULT_PRIVATE_EXPOSURE);

  // ── Image popup state ─────────────────────────────────────────────────
  const [imagePopupOpen,  setImagePopupOpen]  = useState(false);
  const [imageVisibility, setImageVisibility] = useState("public");  // 'public' | 'friends' | 'none'
  const [virtualImage,    setVirtualImage]    = useState(null);
  const [allowMaximize,   setAllowMaximize]   = useState(true);

  // ── Toast state ───────────────────────────────────────────────────────
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const handlePublicFormChange = (field, value) =>
    setPublicForm(prev => ({ ...prev, [field]: value }));

  const handleVisibilityToggle = (field) => {
    setPublicVisibility(prev => {
      const next = !prev[field];
      setToast({
        message: next
          ? `✅ "${field}" is now visible to others.`
          : `🙈 "${field}" is now hidden from others.`,
        type: next ? "success" : "warning",
      });
      return { ...prev, [field]: next };
    });
  };

  const handlePrivateExposureToggle = (field) => {
    if (!privateExposure[field]) {
      const confirmed = window.confirm(
        `${PRIVATE_RISK_MESSAGES[field]}\n\nDo you want to proceed?`
      );
      if (!confirmed) return;
      setToast({ message: `⚠️ "${field}" is now exposed.`, type: "warning" });
    } else {
      setToast({ message: `🔒 "${field}" is back to private.`, type: "success" });
    }
    setPrivateExposure(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // ── Image popup save ──────────────────────────────────────────────────
  const handleImageSettingsSave = ({ imageVisibility: vis, virtualImage: vImg, allowMaximize: aMax }) => {
    setImageVisibility(vis);
    setVirtualImage(vImg);
    setAllowMaximize(aMax);
    setImagePopupOpen(false);
    setToast({ message: "✅ Image settings saved.", type: "success" });
  };

  const handleSubmit = () => {
    console.log("📤 Submitting Self Details:", {
      userid, publicForm, publicVisibility,
      imageSettings: { imageVisibility, virtualImage, allowMaximize },
      privateExposure,
    });
    setToast({ message: "✅ Details saved successfully.", type: "success" });
  };

  const handleCancel = () => {
    setPublicForm({ name: "", location: "", image: null, friends: "0", groups: "0", posts: "0" });
    setPublicVisibility(DEFAULT_PUBLIC_VISIBILITY);
    setPrivateExposure(DEFAULT_PRIVATE_EXPOSURE);
    setToast({ message: "↩️ Changes discarded.", type: "warning" });
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="st-panel sd-root">

      <h2 className="st-panel-title">Self Details</h2>
      <p  className="st-panel-subtitle">
        Manage what others see about you. Toggle visibility to control your public footprint,
        or selectively expose private data — <strong>at your own risk</strong>.
      </p>

      {/* ── Toast ─────────────────────────────────────────────────────── */}
      {toast && (
        <div className={`sd-toast sd-toast--${toast.type}`}>
          {toast.message}
          <button className="sd-toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          SECTION 1 — PUBLIC DATA
          ══════════════════════════════════════════════════════════════ */}
      <div className="sd-section">
        <SectionHeader
          icon="🌐"
          title="Public Information"
          subtitle="This data may be visible to other users. Use the toggles to hide individual fields."
          badge="Visible by default"
        />

        {/* ── Profile image + clickable settings trigger ──────────── */}
        <div className="sd-image-block">
          <div className="sd-image-block-left">
            <span
              className="st-form-label"
              style={{
                fontSize: 13, fontWeight: 600,
                color: "rgba(255,255,255,0.55)",
                textTransform: "uppercase", letterSpacing: "0.06em"
              }}
            >
              Profile Image
            </span>
            <ImageUploader
              currentImage={virtualImage || userPic}
              onImageChange={(val) => handlePublicFormChange("image", val)}
            />
          </div>

          {/* ✅ Clickable trigger replacing the old VisibilityRow */}
          <div className="sd-image-block-right">
            <ImageVisibilityTrigger
              imageVisibility={imageVisibility}
              allowMaximize={allowMaximize}
              onOpen={() => setImagePopupOpen(true)}
            />
          </div>
        </div>

        {/* ── Name ────────────────────────────────────────────────── */}
        <div className="sd-editable-field-block">
          <div className="sd-editable-field-inputs">
            <div className="st-form-row" style={{ marginBottom: 0 }}>
              <label className="st-form-label">Full Name</label>
              <input
                className="st-form-input"
                value={publicForm.name}
                onChange={e => handlePublicFormChange("name", e.target.value)}
                placeholder="Enter your full name"
              />
            </div>
          </div>
          <VisibilityRow
            label="Show Name" fieldKey="name"
            isVisible={publicVisibility.name}
            onToggle={handleVisibilityToggle}
            icon="👤"
            sublabel="Your display name shown to other users"
          />
        </div>

        {/* ── Location ────────────────────────────────────────────── */}
        <div className="sd-editable-field-block">
          <div className="sd-editable-field-inputs">
            <div className="st-form-row" style={{ marginBottom: 0 }}>
              <label className="st-form-label">Location</label>
              <input
                className="st-form-input"
                value={publicForm.location}
                onChange={e => handlePublicFormChange("location", e.target.value)}
                placeholder="City, Country"
              />
            </div>
          </div>
          <VisibilityRow
            label="Show Location" fieldKey="location"
            isVisible={publicVisibility.location}
            onToggle={handleVisibilityToggle}
            icon="📍"
            sublabel="Approximate location shown on your profile"
          />
        </div>

        {/* ── Stats ───────────────────────────────────────────────── */}
        <div className="sd-stats-block">
          <div className="sd-stats-grid">
            {[
              { key: "friends", icon: "👥", label: "Friends" },
              { key: "groups",  icon: "🏘️",  label: "Groups"  },
              { key: "posts",   icon: "📝", label: "Posts"   },
            ].map(s => (
              <div key={s.key} className="sd-stat-card">
                <span className="sd-stat-icon">{s.icon}</span>
                <input
                  className="st-form-input sd-stat-input"
                  value={publicForm[s.key]}
                  onChange={e => handlePublicFormChange(s.key, e.target.value)}
                  type="number" min="0" placeholder="0"
                />
                <span className="sd-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
          <div className="sd-stats-toggles">
            {[
              { key: "friends", icon: "👥", label: "Show Friends Count" },
              { key: "groups",  icon: "🏘️",  label: "Show Groups Count"  },
              { key: "posts",   icon: "📝", label: "Show Posts Count"   },
            ].map(s => (
              <VisibilityRow
                key={s.key} label={s.label} fieldKey={s.key}
                isVisible={publicVisibility[s.key]}
                onToggle={handleVisibilityToggle}
                icon={s.icon}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          SECTION 2 — PRIVATE DATA
          ══════════════════════════════════════════════════════════════ */}
      <div className="sd-section sd-section--private" style={{background: "rgba(36,36,45,0.2)", border: "1px solid gray" }}>
        <SectionHeader
          icon="🔒"
          title="Private Information"
          subtitle="Hidden from all users by default. Exposing any field carries significant privacy risk."
          badge="Protected"
        />

        <div className="sd-private-warning" style={{background: "black", border: "1px solid darkslategray"}}>
          <span className="sd-warning-icon">⚠️</span>
          <p className="sd-warning-text">
            Enabling exposure on any private field will make that information visible to others.
            You will be asked to confirm each action. <strong>Proceed with caution.</strong>
          </p>
        </div>

        {[
          { key: "profileList",    icon: "📋", label: "Profile List",    sublabel: privateData.profileList    },
          { key: "accountDetails", icon: "💳", label: "Account Details", sublabel: privateData.accountDetails },
          { key: "emailId",        icon: "✉️", label: "Email Address",   sublabel: privateData.emailId        },
          { key: "mobileNumber",   icon: "📱", label: "Mobile Number",   sublabel: privateData.mobileNumber   },
        ].map(f => (
          <PrivateRow
            key={f.key} label={f.label} fieldKey={f.key}
            isExposed={privateExposure[f.key]}
            onToggle={handlePrivateExposureToggle}
            icon={f.icon} sublabel={f.sublabel}
          />
        ))}

        {Object.values(privateExposure).some(Boolean) && (
          <div className="sd-exposure-summary">
            <span className="sd-exposure-summary-icon">🚨</span>
            <div>
              <p className="sd-exposure-summary-title">You have exposed private data</p>
              <p className="sd-exposure-summary-text">
                {Object.entries(privateExposure).filter(([, v]) => v).map(([k]) => k).join(", ")}
                {" "}— toggle off above to restore privacy.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Action bar ─────────────────────────────────────────────────── */}
      <div className="sd-action-bar">
        <button className="sd-cancel-btn" type="button" onClick={handleCancel}>↩ Cancel</button>
        <button className="sd-submit-btn" type="button" onClick={handleSubmit}>💾 Save Details</button>
      </div>

      {/* ── Image Settings Popup ───────────────────────────────────────── */}
      {imagePopupOpen && (
        <ImageSettingsPopup
          initialVisibility={imageVisibility}
          initialVirtualImage={virtualImage}
          initialAllowMaximize={allowMaximize}
          onSave={handleImageSettingsSave}
          onClose={() => setImagePopupOpen(false)}
        />
      )}

    </div>
  );
};

export default SelfDetail;
